import Papa from 'papaparse';

import {
  findHeaderMismatch,
  normalizeHeader,
  readCsvHeaders,
} from 'utils/csvHeaders';

import { ResetDepotRow } from '../types';

// Column headers reduced to letters/digits only, so matching tolerates the
// template's casing, spaces, the required-field `*`, `#`, `.`, and any BOM
// (e.g. "FMS ID*", "Unit #*", "VIN*"). There is no Account column, unlike the
// Bulk Add template: FMS IDs resolve against the depots in the reset -- the
// account's depots for an account-scoped run, or the single depot for a
// depot-scoped one.
const COLUMN_HEADERS = {
  fmsId: 'fmsid',
  vin: 'vin',
  unitNumber: 'unit',
  odometer: 'odometer',
  engineHours: 'enginehours',
};

// The columns of the current template, in the order the template prints them.
// Uploads must match this set exactly; see findHeaderMismatch.
export const TEMPLATE_COLUMNS = [
  'FMS ID*',
  'VIN*',
  'Unit #*',
  'Odometer',
  'Engine Hours',
];

/** Resolves to the columns that do not line up with the current template. */
export const findTemplateMismatch = async (file: File) =>
  findHeaderMismatch(await readCsvHeaders(file), TEMPLATE_COLUMNS);

// GraphQL Int is 32-bit signed. A larger value fails coercion and rejects the
// entire planDepotReset mutation rather than the one offending row.
const MAX_INT = 2147483647;

const cell = (
  row: Record<string, unknown>,
  normalizedHeader: string,
): string => {
  const key = Object.keys(row).find(
    (k) => normalizeHeader(k) === normalizedHeader,
  );
  return key ? String(row[key] ?? '').trim() : '';
};

const text = (value: string): string | null => value || null;

const toInteger = (value: string): number | undefined => {
  const normalized = value.replace(/[,\s]/g, '');
  if (!normalized || !/^\d+$/.test(normalized)) return undefined;

  const parsed = Number(normalized);
  return parsed > MAX_INT ? undefined : parsed;
};

/**
 * Filters no row that names anything: the API computes removals from absence,
 * so a dropped row deletes an asset. Blank cells become null so a malformed
 * row still round-trips to the user instead of disappearing.
 */
export const parseResetDepotCsv = (file: File): Promise<ResetDepotRow[]> =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      // 'greedy', not true: a spreadsheet leaves delimiter-only lines behind
      // when rows are deleted, and true keeps those as blank rows -- enough
      // for an emptied file to slip past validateResetDepotFile's row count.
      skipEmptyLines: 'greedy',
      complete: (result) => {
        // A `Quotes` error means Papaparse folded multiple rows into one
        // field, silently losing rows -- which under reset semantics reads as
        // real assets vanishing. This must reject. `FieldMismatch` (ragged
        // column counts) is benign: `header: true` still yields one row per
        // line, just with some cells missing.
        const hasQuoteError = result.errors.some(
          (error) => error.type === 'Quotes',
        );
        if (hasQuoteError) {
          reject(
            new Error(
              'Failed to parse CSV: malformed quoting caused rows to be lost',
            ),
          );
          return;
        }

        resolve(
          (result.data as Record<string, unknown>[]).map((raw, index) => ({
            rowNumber: index + 1,
            fmsId: text(cell(raw, COLUMN_HEADERS.fmsId)),
            vin: text(cell(raw, COLUMN_HEADERS.vin)),
            unitNumber: text(cell(raw, COLUMN_HEADERS.unitNumber)),
            mileage: toInteger(cell(raw, COLUMN_HEADERS.odometer)),
            engineHours: toInteger(cell(raw, COLUMN_HEADERS.engineHours)),
          })),
        );
      },
      error: (error) =>
        reject(new Error(`Failed to parse CSV: ${error.message}`)),
    });
  });
